import { CopyTextButton } from '@backstage/core-components';
import { useSyntaxHighlighter } from '../hooks';
import { makeStyles, useTheme } from '@material-ui/core';
import { SyntaxHighlighterQuery } from '@appfolio/backstage-plugin-syntax-highlighter-common';
import {
  SYNTAX_HIGHLIGHTER_DEFAULT_DARK_THEME,
  SYNTAX_HIGHLIGHTER_DEFAULT_LIGHT_THEME,
} from '@appfolio/backstage-plugin-syntax-highlighter-common';

const useStyles = makeStyles({
  buttons: {
    height: '2.5rem',
    width: '2.5rem',
    opacity: '0.75',
    position: 'absolute',
    top: 0,
    right: 0,
  },
  container: {
    '& code': {
      counterReset: 'step',
      counterIncrement: 'step 0',
    },
    '& code .line::before': {
      content: 'counter(step)',
      counterIncrement: 'step',
      width: '1rem',
      marginRight: '1.5rem',
      display: 'inline-block',
      textAlign: 'right',
      opacity: '0.25',
    },
  },
});

type CodeSnippetProps = Omit<SyntaxHighlighterQuery, 'theme'> & {
  theme?: string;
  onLoaded?: () => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  isCopyButtonEnabled?: boolean;
};

export const CodeSnippet = (props: CodeSnippetProps) => {
  const {
    onLoaded,
    onError,
    onSuccess,
    isCopyButtonEnabled = true,
    ...query
  } = props;
  const theme = useTheme();
  const classes = useStyles();
  const { output, isError, isLoading, isFetching, error } =
    useSyntaxHighlighter({
      ...query,
      theme:
        props.theme ?? theme.palette.type === 'dark'
          ? SYNTAX_HIGHLIGHTER_DEFAULT_DARK_THEME
          : SYNTAX_HIGHLIGHTER_DEFAULT_LIGHT_THEME,
    });
  if (isLoading || isFetching) {
    return null;
  }
  if (onLoaded) {
    onLoaded();
  }
  if (!isError) {
    if (onSuccess) {
      onSuccess();
    }
    return (
      <>
        <div
          className={classes.container}
          dangerouslySetInnerHTML={{ __html: output?.html ?? '<code />' }}
        />
        {isCopyButtonEnabled ? (
          <div className={classes.buttons}>
            <CopyTextButton text={query.code} tooltipText="Copied" />
          </div>
        ) : null}
      </>
    );
  }
  if (onError) {
    onError(error);
  }
  return null;
};
